// Types for the Compliance Frontend Application

// Subscription tier types
export type SubscriptionTier = 'free' | 'basic' | 'pro' | 'enterprise';

export const SUBSCRIPTION_TIERS: SubscriptionTier[] = ['free', 'basic', 'pro', 'enterprise'];

export const TIER_DISPLAY_NAMES: Record<SubscriptionTier, string> = {
  free: 'Free',
  basic: 'Basic',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

export const TIER_BADGE_COLORS: Record<SubscriptionTier, string> = {
  free: '#6b7280',      // gray
  basic: '#3b82f6',     // blue
  pro: '#8b5cf6',       // purple
  enterprise: '#f59e0b', // amber/gold
};

export interface User {
  id: string;
  email: string;
  fullName?: string;
  project?: string;
  projectId?: string;
  role?: 'admin' | 'member';
  subscriptionTier?: SubscriptionTier;
}

export interface UserContext {
  user: { id: string; email: string };
  profile: { id: string; email: string; full_name: string } | null;
  membership: { id: string; role: string; joined_at: string } | null;
  project: {
    id: string;
    domain: string;
    company_name: string;
    industry?: string;
    settings?: Record<string, any>;
    member_count?: number;
    project_slug?: string;
    description?: string;
    response_verbosity?: string;
    qa_url_handling?: string;
    always_include_policy_context?: boolean;
    subscription_tier?: SubscriptionTier;
  } | null;
  needs_profile: boolean;
  needs_project_setup: boolean;
}

export interface Invite {
  id: string;
  email: string;
  role: 'admin' | 'member';
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  created_at: string;
  expires_at: string;
  accepted_at?: string;
}

export interface Project {
  project_id: string;
  company_name: string;
  description: string | null;
  verbosity: string;
  has_policies: boolean;
  has_qa_database: boolean;
  has_urls: boolean;
  subscription_tier?: SubscriptionTier;
}

export interface CustomField {
  name: string;
  description: string;
  field_type: 'text' | 'yes_no' | 'choice' | 'list' | 'number';
  choices?: string[];
}

export interface CSVJobStatus {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  total: number;
  current_query: string | null;
  error: string | null;
  created_at: string;
}

// Source info with URL for batch results
export interface BatchSourceInfo {
  doc_name: string;
  url: string | null;
}

// Debug info for batch results
export interface BatchRAGChunkDebug {
  doc_name: string;
  text_preview: string;
  similarity_score: number;
  url?: string;
}

export interface BatchQAMatchDebug {
  matched_question: string;
  similarity_score: number;
  client_name: string;
  response_date: string;
}

export interface BatchDebugInfo {
  policy_chunks: BatchRAGChunkDebug[];
  qa_matches: BatchQAMatchDebug[];
  qa_match_used: boolean;
  llm_prompt?: string;  // Full prompt sent to LLM (for debugging)
}

// API response types for batch results
export interface BatchResultRow {
  query: string;
  response: string;
  evidence: string[];
  confidence: number;
  sources: BatchSourceInfo[];
  error: string | null;
  custom_fields: Record<string, any>;
  original_data: Record<string, string>;
  debug_info?: BatchDebugInfo;
}

export interface BatchResults {
  job_id: string;
  status: string;
  results: BatchResultRow[];
}

// Frontend processed row (for display/editing)
export interface ProcessedRow {
  id: string;
  query: string;
  response: string;
  evidence: string;
  confidence: number;
  sources: BatchSourceInfo[];
  customFields: Record<string, string>;
  originalData: Record<string, string>;
  isEdited: boolean;
  debugInfo?: BatchDebugInfo;
}

// AuthContextType is now defined in context/AuthContext.tsx

// Chat source types - can be different formats from backend
export interface DocSource {
  doc_name: string;
  url?: string;
  relevance_score?: number;
}

export interface QASource {
  type: 'previous_qa';
  client: string;
  date?: string;
}

export type ChatSource = DocSource | QASource | string;

// Chat types
export interface ChatMessage {
  id: string;
  type: 'user' | 'bot' | 'system' | 'error';
  content: string;
  timestamp: Date;
  confidence?: number;
  sources?: ChatSource[];
  customFields?: Record<string, any>;
  downloadJobId?: string; // For CSV batch download button
  debugInfo?: DebugInfo; // RAG debug info when requested
}

export interface ChatRequest {
  message: string;
  project_id: string;
  include_debug?: boolean;
}

// Debug info types
export interface RAGChunkDebugInfo {
  doc_name: string;
  text_preview: string;
  similarity_score: number;
  url?: string;
}

export interface QAMatchDebugInfo {
  matched_question: string;
  similarity_score: number;
  client_name: string;
  response_date: string;
}

export interface DebugInfo {
  policy_chunks: RAGChunkDebugInfo[];
  qa_matches: QAMatchDebugInfo[];
  threshold_used: number;
  qa_match_used: boolean;
  llm_prompt?: string;  // Full prompt sent to LLM (for debugging)
}

export interface ChatResponse {
  response: string;
  source_type: string;
  confidence: number;
  sources: ChatSource[];
  matched_question?: string;
  debug_info?: DebugInfo;
}

export interface StructuredChatRequest {
  question: string;
  project_id: string;
  custom_fields: CustomField[];
}

export interface StructuredChatResponse {
  response: string;
  confidence: number;
  sources: ChatSource[];
  evidence_urls: string[];
  custom_fields: Record<string, any>;
}

// === Knowledge Base Types ===

export interface DocumentInfo {
  filename: string;
  display_name: string;
  public_url: string | null;
  size_bytes: number;
}

export interface DocumentListResponse {
  project_id: string;
  documents: DocumentInfo[];
  total_count: number;
}

export interface DocumentMetadataUpdate {
  display_name?: string;
  public_url?: string;
}

export interface URLMappingEntry {
  filename: string;
  display_name: string;
  public_url: string | null;
  auto_mapped: boolean;
  confidence: number;
}

export interface URLMappingResponse {
  project_id: string;
  mappings: URLMappingEntry[];
  unmapped_files: string[];
  auto_mapped_count: number;
  manual_required_count: number;
}

export interface QADatabaseInfo {
  project_id: string;
  has_database: boolean;
  record_count: number;
  preview: Record<string, string>[];
}

export interface UploadResult {
  project_id: string;
  uploaded: string[];
  uploaded_count: number;
  replaced?: string[];
  replaced_count?: number;
  skipped?: string[];
  skipped_count?: number;
  errors: string[];
  error_count: number;
}

// === Questionnaire Types (Async Processing) ===

export type QuestionnaireStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type QuestionnaireItemStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';

// Track who edited and when
export interface EditHistoryEntry {
  edited_by: string;        // User email or name
  edited_by_name?: string;  // Full name if available
  edited_at: string;        // ISO timestamp
  changes?: string[];       // Optional: what was changed (e.g., ["response", "evidence"])
}

export interface QuestionnaireItem {
  id: string;
  questionnaire_id: string;
  row_number: number;
  original_query: string;
  extra_columns: Record<string, string>;
  status: QuestionnaireItemStatus;
  error_message?: string;
  response?: string;
  evidence: string[];
  confidence?: number;
  custom_field_values: Record<string, any>;
  debug_info?: BatchDebugInfo;
  source_type?: string;
  matched_question?: string;
  last_edited_by?: string;      // Email of last editor
  last_edited_by_name?: string; // Full name of last editor
  last_edited_at?: string;
  is_manually_edited: boolean;
  processed_at?: string;
  edit_history?: EditHistoryEntry[]; // Full history of edits
  // Approval tracking
  is_approved?: boolean | null;  // true=approved, false=needs edit, null=not reviewed
  is_uncertain?: boolean;        // true=marked as "not sure", needs further review
  approved_by?: string;          // Email of approver
  approved_by_name?: string;     // Full name of approver
  approved_at?: string;          // ISO timestamp
  // Q&A Bank save tracking
  is_saved_to_qa?: boolean;      // true=saved to Q&A bank
  saved_to_qa_at?: string;       // ISO timestamp when saved
  saved_to_qa_by?: string;       // Email of user who saved
  saved_to_qa_by_name?: string;  // Full name of user who saved
}

export interface Questionnaire {
  id: string;
  project_id: string;
  client_name: string;
  original_filename: string;
  total_items: number;
  completed_items: number;
  status: QuestionnaireStatus;
  custom_fields: CustomField[];
  uploaded_by?: string;       // Email of uploader
  uploaded_by_name?: string;  // Full name of uploader
  uploaded_at?: string;
  completed_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface QuestionnaireWithItems extends Questionnaire {
  items: QuestionnaireItem[];
}

export interface QuestionnaireListResponse {
  questionnaires: Questionnaire[];
  total: number;
}

export interface CreateQuestionnaireResponse {
  id: string;
  message: string;
  total_items: number;
}

export interface UpdateItemRequest {
  response?: string;
  evidence?: string[];
  custom_field_values?: Record<string, any>;
  edited_by?: string;       // Email of editor
  edited_by_name?: string;  // Full name of editor
}

export interface ApproveItemRequest {
  is_approved?: boolean | null;
  is_uncertain?: boolean;
  approved_by?: string;       // Email of approver
  approved_by_name?: string;  // Full name of approver
}
